import {
  type InvoiceDirection,
  type InvoiceKind,
  type InvoiceState,
  invoiceStateLabel,
} from '@k-management/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

export const treasuryKeys = {
  all: ['treasury'] as const,
  invoices: ['treasury', 'invoices'] as const,
  invoice: (id: string) => ['treasury', 'invoices', id] as const,
  history: (id: string) => ['treasury', 'invoices', id, 'history'] as const,
};

/** Da sistemare (3.00): the summary next to the menu entry, the loads, the items. */
export const worklistKeys = {
  all: ['treasury', 'worklist'] as const,
  summary: ['treasury', 'worklist', 'summary'] as const,
};

const STATE_PILLS: Record<InvoiceState, string> = {
  aperta: 'pill info',
  parziale: 'pill warn',
  scaduta: 'pill bad',
  saldata: 'pill ok',
};

export function InvoiceStatePill({
  state,
  direction,
  kind = 'fattura',
  overdueDays = 0,
  replaced = false,
  cancelled = false,
}: {
  state: InvoiceState;
  direction: InvoiceDirection;
  kind?: InvoiceKind;
  overdueDays?: number;
  /** A pro forma replaced by its invoices (1.59). */
  replaced?: boolean;
  /** Cancelled in K-Management (2.15). */
  cancelled?: boolean;
}) {
  if (cancelled) return <span className="pill muted">Annullata</span>;
  if (replaced) return <span className="pill muted">Sostituita</span>;
  // A credit note is used, never late (1.58).
  if (kind === 'nota_credito') {
    return (
      <span className={state === 'saldata' ? 'pill ok' : 'pill warn'}>
        {invoiceStateLabel(state, direction, kind)}
      </span>
    );
  }
  return (
    <span className={STATE_PILLS[state]}>
      {state === 'scaduta' && overdueDays > 0
        ? `Scaduta da ${overdueDays} ${overdueDays === 1 ? 'giorno' : 'giorni'}`
        : invoiceStateLabel(state, direction)}
    </span>
  );
}

/**
 * The windows of the app open in the same browser talk on this channel
 * (07/10/2026): the separate reconciliation window says when it changed
 * something, the page of the movements tells it which movement to show.
 */
const CHANNEL = 'k-management-treasury';

export type TreasuryMessage =
  { type: 'changed' } | { type: 'open'; code: string; movementId: string };

function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
}

export function postTreasuryMessage(message: TreasuryMessage): void {
  const target = channel();
  target?.postMessage(message);
  target?.close();
}

/** Something of the Tesoreria changed here: the other windows reload it. */
export function announceTreasuryChange(): void {
  postTreasuryMessage({ type: 'changed' });
}

/** Reloads the Tesoreria when another window changes it, and hands on the other messages. */
export function useTreasuryChannel(onMessage?: (message: TreasuryMessage) => void): void {
  const queryClient = useQueryClient();
  const handler = useRef(onMessage);
  handler.current = onMessage;
  useEffect(() => {
    const source = channel();
    if (!source) return;
    source.onmessage = (event: MessageEvent<TreasuryMessage>) => {
      if (event.data.type === 'changed')
        void queryClient.invalidateQueries({ queryKey: ['treasury'] });
      else handler.current?.(event.data);
    };
    return () => source.close();
  }, [queryClient]);
}

/**
 * The page of a bank account showing only one of its movements (2.07): the
 * links from invoices, cards and loans, which used to open the whole list.
 */
export function movementPath(code: string, movementId: string): string {
  return `/tesoreria/banche/${encodeURIComponent(code)}?movimento=${encodeURIComponent(movementId)}`;
}

/** Whether a document is a credit note, to mark it in the lists (2.12). */
export function isCreditNote(item: { kind?: InvoiceKind | null }): boolean {
  return item.kind === 'nota_credito';
}

/** The class of the row of a credit note in a list (2.12). */
export function creditNoteRow(item: { kind?: InvoiceKind | null }): string | undefined {
  return isCreditNote(item) ? 'credit-note-row' : undefined;
}

/** The «NC» of a credit note next to its number, the same in every list (2.12). */
export function CreditNoteMark({ kind }: { kind?: InvoiceKind | null }) {
  if (kind !== 'nota_credito') return null;
  return (
    <span className="pill credit-note" title="Nota di credito">
      NC
    </span>
  );
}
